import React, { useState } from "react";
import { useAdmin } from "@/context/AdminContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { adminAxios } from "@/lib/adminAuth";
import { toast } from "sonner";
import { MessagesSquare, TrendingUp, Users, RefreshCw } from "lucide-react";

const StatCard = ({ label, value, icon: Icon }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4">
    <div className="w-10 h-10 rounded-xl bg-brand-green/10 flex items-center justify-center flex-shrink-0">
      <Icon className="h-5 w-5 text-brand-green" />
    </div>
    <div>
      <p className="text-xs font-bold uppercase tracking-widest text-brand-blue">
        {label}
      </p>
      <p className="font-heading font-extrabold text-2xl text-slate-900 mt-0.5">
        {value ?? "—"}
      </p>
    </div>
  </div>
);

const AdminChatbotPage = () => {
  const { chats, chatbotStats } = useAdmin();
  const [openSession, setOpenSession] = useState(null);

  const openDetail = async (sid) => {
    try {
      const res = await adminAxios().get(`/admin/chats/${sid}`);
      setOpenSession(res.data);
    } catch {
      toast.error("Erreur de chargement.");
    }
  };

  const topService = chatbotStats?.top_services_mentioned
    ? Object.entries(chatbotStats.top_services_mentioned).sort((a, b) => b[1] - a[1])[0]?.[0]
    : null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Sessions totales"
          value={chatbotStats?.total_sessions}
          icon={MessagesSquare}
        />
        <StatCard
          label="Messages / session"
          value={
            chatbotStats?.avg_messages_per_session
              ? chatbotStats.avg_messages_per_session.toFixed(1)
              : "—"
          }
          icon={Users}
        />
        <StatCard
          label="Taux conversion"
          value={
            chatbotStats?.conversion_rate != null
              ? `${Math.round(chatbotStats.conversion_rate * 100)}%`
              : "—"
          }
          icon={TrendingUp}
        />
        <StatCard label="Top service" value={topService || "—"} icon={RefreshCw} />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left text-xs font-semibold uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Langue</th>
              <th className="px-4 py-3">Messages</th>
              <th className="px-4 py-3">Converti</th>
              <th className="px-4 py-3">Premier message</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {chats.map((c) => (
              <tr key={c.session_id} data-testid={`chat-row-${c.session_id}`}>
                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                  {(c.updated_at || "").slice(0, 16).replace("T", " ")}
                </td>
                <td className="px-4 py-3 uppercase text-slate-600">
                  {c.language || "fr"}
                </td>
                <td className="px-4 py-3 text-slate-600">{c.message_count}</td>
                <td className="px-4 py-3">
                  {c.converted ? (
                    <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                      Oui
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-400 max-w-[260px] truncate">
                  {c.first_user_message}
                </td>
                <td className="px-4 py-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    onClick={() => openDetail(c.session_id)}
                  >
                    Voir
                  </Button>
                </td>
              </tr>
            ))}
            {chats.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                  Aucune conversation.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!openSession} onOpenChange={(v) => !v && setOpenSession(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Conversation #{openSession?.session_id?.slice(0, 8)}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-1">
            {(openSession?.messages || []).map((m, i) => (
              <div
                key={i}
                className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed max-w-[85%] ${
                  m.role === "assistant"
                    ? "bg-slate-100 text-slate-800 mr-auto"
                    : "bg-brand-green text-white ml-auto"
                }`}
              >
                {m.content}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminChatbotPage;
